import React, { useState, useEffect, useMemo } from 'react'
import { Canvas } from '@react-three/fiber'
import './unifiedUI.scss'
import AboutOverlay from './AboutOverlay.jsx'
import OverlayNavigation from './overlayNavigation.jsx'
import IntroTextBox from './IntroTextBox.jsx'
import aboutDataJson from './aboutData.json'

/**
 * aboutGallery Component
 * Two states: 'closed' | 'open'
 * Animations: slide up from bottom (open), slide down to bottom (close)
 * 
 * Single phase with 4 sections: Introduction, Experience, Skills, Achievements
 */
const aboutGallery = ({ state, onRequestClose }) => {
  const [animationState, setAnimationState] = useState('closed') // 'closed' | 'opening' | 'open' | 'closing'
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [showIntroOverlay, setShowIntroOverlay] = useState(false)
  const [isFadingOut, setIsFadingOut] = useState(false)
  
  // Handle state changes from parent
  // Proper 4-state machine: closed → opening → open → closing → closed
  useEffect(() => {
    if (state === 'open' && animationState === 'closed') {
      // Start opening animation (only from closed state)
      setAnimationState('opening')
      setCurrentItemIndex(0)
      
      // After animation completes, set to open
      const timer = setTimeout(() => {
        setAnimationState('open')
      }, 500) // Match CSS animation duration
      
      return () => clearTimeout(timer)
    } else if (state === 'closed' && animationState === 'open') {
      // Start closing animation (only from open state)
      setAnimationState('closing')
      
      // After animation completes, set to closed
      const timer = setTimeout(() => {
        setAnimationState('closed')
      }, 500) // Match CSS animation duration
      
      return () => clearTimeout(timer)
    }
  }, [state]) // REMOVED animationState from dependencies

  // Handle ESC key - close overlay (aboutGallery is always level 1)
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key !== 'Escape') return
      
      // Only handle if overlay is open
      const isOpen = animationState === 'open'
      if (!isOpen) return
      
      // Stop the event from propagating to other handlers
      event.preventDefault()
      event.stopPropagation()
      
      console.log('ESC: Closing about overlay')
      onRequestClose()
    }

    window.addEventListener('keydown', handleEscKey, true) // USE CAPTURE PHASE
    return () => window.removeEventListener('keydown', handleEscKey, true)
  }, [animationState, onRequestClose])

  // Breadcrumb path for navigation
  const breadcrumbPath = [
    { label: 'ABOUT', level: 1 }
  ]

  // Calculate max items (total steps across all sections)
  const maxItems = useMemo(() => {
    const phaseData = aboutDataJson['about-phase']
    if (!phaseData) return 0
    
    let count = 0
    phaseData.sections?.forEach(section => {
      section.subsections?.forEach(subsection => {
        count += subsection.steps?.length || 0
      })
    })
    return count
  }, [])

  // Get current subsection info for intro overlay
  const currentSubsectionInfo = useMemo(() => {
    const phaseData = aboutDataJson['about-phase']
    if (!phaseData) return null
    
    let stepCounter = 0
    for (const section of phaseData.sections || []) {
      for (const subsection of section.subsections || []) {
        const stepsCount = subsection.steps?.length || 0
        if (currentItemIndex >= stepCounter && currentItemIndex < stepCounter + stepsCount) {
          return {
            section: section.sectionTitle,
            subsection: subsection,
            isFirstStep: currentItemIndex === stepCounter
          }
        }
        stepCounter += stepsCount
      }
    }
    return null
  }, [currentItemIndex])

  // Handle intro overlay fade in/out
  useEffect(() => {
    if (currentSubsectionInfo) {
      setIsFadingOut(false)
      setShowIntroOverlay(true)
      
      // Auto-hide after 5 seconds for non-first steps
      if (!currentSubsectionInfo.isFirstStep) {
        const timer = setTimeout(() => {
          setIsFadingOut(true)
          setTimeout(() => {
            setShowIntroOverlay(false)
            setIsFadingOut(false)
          }, 500)
        }, 5000)
        return () => clearTimeout(timer)
      }
    }
  }, [currentSubsectionInfo])

  // Handle scroll/wheel events for navigation
  useEffect(() => {
    console.log('About wheel handler setup, animationState:', animationState, 'maxItems:', maxItems)
    if (animationState !== 'open') return

    let isNavigating = false
    let navigationTimeout = null
    let touchStartX = 0
    let touchStartY = 0
    let touchStartTime = 0

    const navigate = (direction) => {
      if (isNavigating) return
      
      const targetIndex = currentItemIndex + direction
      
      if (targetIndex < 0 || targetIndex >= maxItems) {
        console.log('Target index out of bounds:', targetIndex)
        return
      }

      console.log('Navigating to index:', targetIndex)
      isNavigating = true
      setCurrentItemIndex(targetIndex)

      if (navigationTimeout) {
        clearTimeout(navigationTimeout)
      }

      navigationTimeout = setTimeout(() => {
        isNavigating = false
      }, 250) // Standard cooldown
    }

    const handleWheel = (e) => {
      e.preventDefault()
      
      console.log('About wheel event:', { deltaY: e.deltaY, isNavigating, currentItemIndex, maxItems })
      
      const direction = e.deltaY > 0 ? 1 : -1
      navigate(direction)
    }

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX
      touchStartY = e.touches[0].clientY
      touchStartTime = Date.now()
    }

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX
      const touchEndY = e.changedTouches[0].clientY
      const touchDuration = Date.now() - touchStartTime
      
      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      
      // Only trigger if swipe is primarily VERTICAL and fast enough
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 50 && touchDuration < 500) {
        const direction = deltaY > 0 ? -1 : 1 // Swipe down = previous, swipe up = next
        navigate(direction)
      }
    }

    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    
    return () => {
      console.log('Removing about wheel handler')
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
      if (navigationTimeout) {
        clearTimeout(navigationTimeout)
      }
    }
  }, [animationState, maxItems, currentItemIndex])

  // Don't render if closed
  if (animationState === 'closed') return null

  // Determine CSS class based on animation state
  const getClassName = () => {
    if (animationState === 'opening') return 'canvas-work-gallery opening'
    if (animationState === 'closing') return 'canvas-work-gallery closing'
    return 'canvas-work-gallery open'
  }

  return (
    <div className={getClassName()}>
      <Canvas
        linear
        flat
        camera={{
          fov: 50,
          near: 0.1,
          far: 1000,
          position: [0, 0, 15]
        }}
        dpr={[1, 2]}
        style={{ 
          width: '100vw', 
          height: '100vh',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          background: 'linear-gradient(180deg, #5d728eff 0%, #687d99ff 100%)',
          backgroundSize: '100% 100%'
        }}
      >
        <AboutOverlay
          key="about-scene"
          currentItemIndex={currentItemIndex}
          onNavigate={(index) => setCurrentItemIndex(index)}
        />
      </Canvas>
      
      {/* Noise overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        opacity: 0.4,
        mixBlendMode: 'overlay',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 200px'
      }} />

      {/* Intro Text Overlay - separate component for mobile portrait layout */}
      {showIntroOverlay && currentSubsectionInfo && (
        <IntroTextBox 
          subsectionInfo={currentSubsectionInfo}
          isFadingOut={isFadingOut}
        />
      )}

      {/* Navigation UI overlay - full viewport for controls */}
      <div className="canvas-ui-overlay">
        <OverlayNavigation
          breadcrumbPath={breadcrumbPath}
          onBreadcrumbClick={() => {}}
          currentItemIndex={currentItemIndex}
          maxItems={maxItems}
          onClose={onRequestClose}
        />

        {/* Step Indicator - hidden for about overlay */}
        {/* <StepIndicator
          projectData={aboutDataJson['about-phase']}
          currentItemIndex={currentItemIndex}
          currentLevel={1}
          onNavigate={(index) => setCurrentItemIndex(index)}
        /> */}
      </div>
    </div>
  )
}

export default aboutGallery
